import type { ViewRecordFieldChange } from '@salescore/client-redux'
import type { ModelSearcher, NodePath, ViewQueryField, ViewQueryList, ViewQueryRecordNode } from '@salescore/core'
import { logger } from '@salescore/frontend-common'
import { Array, Order, pipe, Result } from 'effect'
import { isUndefined } from 'effect/Predicate'

import { getInnerRecordNodePure, getRecordNodeFromRootNodePure } from '../../state/nodeUtility'
import { type DeepReadonly, toMutable } from '../types/DeepReadonly'
import { destroyChildRecord, type DestroyChildRecordLeft } from './destroyChildRecord'
import { destroyRootRecord, type DestroyRootRecordLeft } from './destroyRootRecord'
import { type UpdateChildViewRecordLeft, updateChildViewRecordPure } from './updateChildViewRecord'
import { updateRootViewRecord, type UpdateRootViewRecordLeft } from './updateRootViewRecord'

export interface RecordChange {
  rowIndex: number // 何行目か
  innerRowIndex: number // 何行目か(内部行)
  nodePath: NodePath // ViewQueryNodeのパス
  field: ViewQueryField // フィールド情報
  value: unknown // 変更した値
  label: string | undefined // 参照項目の場合、変更後の値だけでなく変更後のラベルもchangeに渡す
  isDelete?: boolean // 削除の場合はtrue
  isDeleteAllChildren?: boolean // 自身の子レコードを全て削除する場合はtrue
  isNestedColumn: boolean // 内部行ならtrue（nodePathから求められるはず？不要な値な気がする）
}

const sortRecordChanges = (recordChanges: DeepReadonly<RecordChange[]>): DeepReadonly<RecordChange[]> =>
  Array.every(recordChanges, (x) => x.isDelete === true)
    ? // （複数行）削除の場合は、 records の破壊的操作によりインデックスが崩れるのを防止するため、削除する行のインデックスが大きい順に削除する
      Array.sortWith(
        recordChanges,
        (recordChange) => recordChange.rowIndex * 1000 * 1000 + recordChange.innerRowIndex,
        Order.flip(Order.Number),
      )
    : recordChanges

interface UpdateViewRecordsPureRight {
  records: DeepReadonly<ViewQueryRecordNode[]>
  results: Array<Result.Result<DeepReadonly<UpdateViewRecordRight>, UpdateViewRecordLeft>>
}
export const updateViewRecords = ({
  initialRecords,
  view,
  recordChanges,
  modelSearcher,
}: {
  initialRecords: DeepReadonly<ViewQueryRecordNode[]>
  view: DeepReadonly<ViewQueryList>
  recordChanges: DeepReadonly<RecordChange[]>
  modelSearcher: ModelSearcher
}): UpdateViewRecordsPureRight =>
  pipe(
    sortRecordChanges(recordChanges),
    Array.reduce<UpdateViewRecordsPureRight, DeepReadonly<RecordChange>>(
      {
        records: initialRecords,
        results: [],
      },
      (accumulator, recordChange) => {
        const result = updateViewRecordPure(recordChange, accumulator.records, view, modelSearcher)
        if (Result.isFailure(result)) {
          return {
            records: accumulator.records,
            results: [...accumulator.results, Result.fail(result.failure)],
          }
        }
        switch (result.success.rootChangeType) {
          case 'delete': {
            return {
              records: accumulator.records.filter((record) => record.id !== result.success.recordNode.id),
              results: [...accumulator.results, Result.succeed(result.success)],
            }
          }
          case 'update':
          case 'none': {
            return {
              records: accumulator.records.map((record) =>
                record.id === result.success.recordNode.id ? result.success.recordNode : record,
              ),
              results: [...accumulator.results, Result.succeed(result.success)],
            }
          }
        }
      },
    ),
  )

export interface UpdateViewRecordRight {
  rootChangeType: 'update' | 'delete' | 'none' // none は子孫レコードが変化した場合
  recordNode: ViewQueryRecordNode
  changes: ViewRecordFieldChange[]
}

export type UpdateViewRecordLeft =
  | DestroyRootRecordLeft
  | DestroyChildRecordLeft
  | UpdateRootViewRecordLeft
  | UpdateChildViewRecordLeft

/**
 *
 * recordChange に基づき、新規レコード追加、更新、削除を行う
 * ルートレコードでも子レコードでも対応可能
 */
function updateViewRecordPure(
  recordChange: DeepReadonly<RecordChange>,
  records: DeepReadonly<ViewQueryRecordNode[]>,
  query: DeepReadonly<ViewQueryList>,
  modelSearcher: ModelSearcher,
): Result.Result<DeepReadonly<UpdateViewRecordRight>, UpdateViewRecordLeft> {
  const { rowIndex, innerRowIndex, field } = recordChange

  // 今回の操作の対象となるルートのレコード(行)を取得
  const rootRecordNode = toMutable(records[rowIndex])
  if (isUndefined(rootRecordNode)) {
    logger.warn('SHEET_CELL_EDIT', 'updateViewRecord で rootRecordNode が見つかりませんでした')
    return Result.fail(undefined)
  }

  // レコードの中の対象ノードを取得
  // 親要素の時レコード、子の時はレコードの配列
  const targetRecordNode = getRecordNodeFromRootNodePure(rootRecordNode, field.nodePath)

  // 対象ノードが存在しないことはありえないが、一応ガードしておく
  if (targetRecordNode === undefined) {
    logger.warn('SHEET_CELL_EDIT', 'updateViewRecord で targetRecordNode が見つかりませんでした')
    return Result.fail(undefined)
  }
  if (Array.isArray(targetRecordNode)) {
    //
    // 子（ネストしたセル）の場合
    //
    const childRecordNode = getInnerRecordNodePure(targetRecordNode, innerRowIndex)
    if (recordChange.isDelete === true) {
      return pipe(
        destroyChildRecord(recordChange, query, childRecordNode, rootRecordNode, modelSearcher),
        Result.match({
          onFailure: (left) => Result.fail(left),
          onSuccess: (right) =>
            Result.succeed({
              rootChangeType: 'none',
              recordNode: right.rootRecordNode,
              changes: right.changes,
            }),
        }),
      )
    }
    return pipe(
      updateChildViewRecordPure(recordChange, query, childRecordNode, rootRecordNode),
      Result.match({
        onFailure: (left) => Result.fail(left),
        onSuccess: ({ newChange, newRootNode }) =>
          Result.succeed({
            rootChangeType: 'none',
            recordNode: newRootNode,
            changes: [newChange],
          }),
      }),
    )
  }

  if (recordChange.isDelete === true) {
    return pipe(
      destroyRootRecord({
        shouldDeleteAllChildren: recordChange.isDeleteAllChildren,
        query,
        deletingRecord: rootRecordNode,
        records,
        modelSearcher,
      }),
      Result.match({
        onFailure: (left) => Result.fail(left),
        onSuccess: (right) =>
          Result.succeed({
            rootChangeType: 'delete',
            recordNode: rootRecordNode,
            changes: right.viewRecordFieldChanges,
          }),
      }),
    )
  }

  return pipe(
    updateRootViewRecord(recordChange, query, rootRecordNode),
    Result.match({
      onFailure: (left) => Result.fail(left),
      onSuccess: (right) =>
        Result.succeed({
          rootChangeType: 'update',
          recordNode: right.rootRecordNode,
          changes: right.viewRecordFieldChanges,
        }),
    }),
  )
}
